import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Pause, Play, RefreshCw, Square } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { agentService } from '@/services/agent';
import { type AgentStatus, useAgentStore } from '@/stores/agentStore';
import { formatCurrency } from '@/lib/utils';

export function AgentControlPanel() {
  const queryClient = useQueryClient();
  const { setAgentState } = useAgentStore();

  const { data: state, isLoading } = useQuery({
    queryKey: ['agent', 'status'],
    queryFn: () => agentService.getStatus(),
    refetchInterval: 5000,
  });

  // Update store when data changes
  useEffect(() => {
    if (state) {
      setAgentState(state);
    }
  }, [state, setAgentState]);

  const startMutation = useMutation({
    mutationFn: () => agentService.start(),
    onSuccess: (data) => {
      setAgentState(data);
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: () => agentService.pause(),
    onSuccess: (data) => {
      setAgentState(data);
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => agentService.resume(),
    onSuccess: (data) => {
      setAgentState(data);
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => agentService.stop(),
    onSuccess: (data) => {
      setAgentState(data);
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });

  const handleStart = () => void startMutation.mutate();
  const handlePause = () => void pauseMutation.mutate();
  const handleResume = () => void resumeMutation.mutate();
  const handleStop = () => void stopMutation.mutate();

  const statusConfig: Record<
    AgentStatus,
    { color: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }
  > = {
    stopped: { color: 'neutral', label: 'Stopped' },
    starting: { color: 'info', label: 'Starting...' },
    active: { color: 'success', label: 'Active' },
    paused: { color: 'warning', label: 'Paused' },
    error: { color: 'error', label: 'Error' },
  };

  const config = statusConfig[state?.status || 'stopped'];
  const isPending =
    startMutation.isPending ||
    pauseMutation.isPending ||
    resumeMutation.isPending ||
    stopMutation.isPending;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse h-32 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">AI Trading Agent</CardTitle>
        <StatusBadge status={config.color}>{config.label}</StatusBadge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          {state?.status === 'stopped' && (
            <Button onClick={handleStart} disabled={isPending} size="sm">
              <Play className="h-4 w-4 mr-1" /> Start
            </Button>
          )}
          {state?.status === 'active' && (
            <Button onClick={handlePause} disabled={isPending} size="sm" variant="outline">
              <Pause className="h-4 w-4 mr-1" /> Pause
            </Button>
          )}
          {state?.status === 'paused' && (
            <Button onClick={handleResume} disabled={isPending} size="sm">
              <RefreshCw className="h-4 w-4 mr-1" /> Resume
            </Button>
          )}
          {(state?.status === 'active' || state?.status === 'paused') && (
            <Button onClick={handleStop} disabled={isPending} size="sm" variant="destructive">
              <Square className="h-4 w-4 mr-1" /> Stop
            </Button>
          )}
        </div>

        <div className="text-sm space-y-1">
          <p>
            <span className="text-muted-foreground">Strategy:</span>{' '}
            {state?.strategyName || 'Default'}
          </p>
          <p>
            <span className="text-muted-foreground">Daily P&L:</span>
            <span className={(state?.dailyPnl ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}>
              {' '}
              {formatCurrency(state?.dailyPnl || 0)}
            </span>
          </p>
          <p>
            <span className="text-muted-foreground">Trades Today:</span> {state?.tradesToday || 0}
          </p>
          {state?.lastAction && (
            <p>
              <span className="text-muted-foreground">Last Action:</span> {state.lastAction}
            </p>
          )}
        </div>

        {state?.errorMessage && (
          <div className="flex items-center gap-2 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            {state.errorMessage}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AgentControlPanel;
